/* eslint-disable no-console */
/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable unused-imports/no-unused-imports */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { IRepository } from '../../infrastructure/repository.interface';
import { WorkspaceModel } from '../models/workspace.model';
import { Injectable } from '@angular/core';
import { CentralDatabaseProvider } from '../central-database.provider';
import { CentralDatabaseKeys } from '../schemas/central-database-keys';
import { RxCollection, RxDatabase, RxDocument } from 'rxdb';
import { CentralDatabaseDbContext } from '../schemas/central-database.dbcontext';
import { from, Observable, of } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';
import { ConfigHeader, ErrorCodes, FileModel, FusionEntity, User } from '@shared';
import { LoggingService } from '@shared/logging';
import { addRxPlugin } from 'rxdb';
import { RxDBQueryBuilderPlugin } from 'rxdb/plugins/query-builder';
import { TableInfoRepository } from './table-info.repository';
import { TableRepository } from '@database/workspace';
import { TableModel } from '@shared';
import { DataRepository } from '@database/workspace';
import { DataModel } from '@shared';
import { WorkspaceDataImporterService } from '@database/workspace';
import { ToastService } from '@shared';
import { TranslateService } from '@ngx-translate/core';
addRxPlugin(RxDBQueryBuilderPlugin);

@Injectable()
export class WorkspaceRepository implements IRepository<WorkspaceModel> {
	private centralDatabase: RxDatabase<CentralDatabaseDbContext>;
	private workspacesCollection: RxCollection<WorkspaceModel>;

	public constructor(
		private dbProvider: CentralDatabaseProvider,
		private loggingService: LoggingService,
		private tableInfoRepository: TableInfoRepository,
		private tableRepository: TableRepository,
		private dataRepository: DataRepository,
		private toastService: ToastService,
		private translateService: TranslateService,
		private workspaceDataImporterService: WorkspaceDataImporterService
	) {
		this.centralDatabase = this.dbProvider.getDatabase();
		this.workspacesCollection = this.centralDatabase[CentralDatabaseKeys.workspaces];
	}

	public async insert(entity: WorkspaceModel): Promise<void> {
		try {
			await this.centralDatabase.workspaces.insert(entity);
		} catch (error) {
			this.loggingService.error(ErrorCodes.WorkspaceDataImportInsertDatabase, error);
			throw new Error('Insert of records failed');
		}
	}

	public async remove(workFileId: string): Promise<void> {
		await this.centralDatabase.workspaces.bulkRemove([workFileId]);
	}

	public async deleteWorkfile(workfileId: string): Promise<void> {
		try {
			// partially deleted workfile
			// make sure there are still records to be deleted and avoid triggering an error
			const workfileRecordExist: boolean = await this.exists(workfileId);
			if (!workfileRecordExist) {
				return;
			}

			const workfile = await this.centralDatabase.workspaces
				.find()
				.where(CentralDatabaseKeys.workFileId)
				.eq(workfileId)
				.remove();

			if (workfile.length === 0) {
				throw new Error('Delete workfile (workspaces) failed');
			}
		} catch (error) {
			throw new Error(error);
		}
	}

	public async bulkInsert(entities: WorkspaceModel[]): Promise<void> {
		await this.centralDatabase.workspaces.bulkInsert(entities);
	}

	public getAll$(): Observable<WorkspaceModel[]> {
		return this.workspacesCollection
			.find()
			.$.pipe(map((entries: RxDocument<any>[]) => entries.map((item) => item.toJSON())));
	}

	public getAllByFusionUserId$(fusionUserId: string | null): Observable<WorkspaceModel[]> {
		if (!fusionUserId) {
			return of([]);
		}

		return this.workspacesCollection
			.find({
				selector: {
					[CentralDatabaseKeys.fusionUsers]: {
						$elemMatch: { [CentralDatabaseKeys.userId]: fusionUserId }
					}
				}
			})
			.$.pipe(map((entries: RxDocument<any>[]) => entries.map((item) => item.toJSON())));
	}

	public hasWorkfilesImported$(): Observable<boolean> {
		return this.getAll$().pipe(map((dbWorkspaces: RxDocument<WorkspaceModel>[]) => dbWorkspaces.length > 0));
	}

	public getUsers$(workFileId: string): Observable<User[]> {
		return this.workspacesCollection
			.findOne()
			.where(CentralDatabaseKeys.workFileId)
			.eq(workFileId)
			.$.pipe(
				map((dbWorkspace: RxDocument<WorkspaceModel>) => (dbWorkspace.toJSON() as WorkspaceModel).fusionUsers)
			);
	}

	public getConfigHeaders$(workFileId: string): Observable<ConfigHeader[]> {
		return this.workspacesCollection
			.findOne()
			.where(CentralDatabaseKeys.workFileId)
			.eq(workFileId)
			.$.pipe(
				map(
					(dbWorkspace: RxDocument<WorkspaceModel>) =>
						(dbWorkspace.toJSON() as WorkspaceModel).fussionConfigHeaders
				)
			);
	}

	public getConnectionId$(workFileId: string): Observable<string | undefined> {
		return this.workspacesCollection
			.findOne()
			.where(CentralDatabaseKeys.workFileId)
			.eq(workFileId)
			.$.pipe(
				map((dbWorkspace: RxDocument<WorkspaceModel>) =>
					// TODO: throwError instead of null value in case of no connection for workfile
					dbWorkspace ? dbWorkspace.toJSON().apiConnectionId : null
				),
				first()
			);
	}

	public async exists(workFileId: string): Promise<boolean> {
		const workFile: RxDocument<WorkspaceModel> = await this.workspacesCollection
			.findOne()
			.where(CentralDatabaseKeys.workFileId)
			.eq(workFileId)
			.exec();

		if (!workFile) {
			return false;
		}

		return true;
	}

	public getWorkFileNameById$(workfileId: string): Observable<string> {
		return this.workspacesCollection
			.findOne()
			.where(CentralDatabaseKeys.workFileId)
			.eq(workfileId)
			.$.pipe(map((workfile: RxDocument<WorkspaceModel>) => workfile.toJSON().workspaceName));
	}

	public async getWorkFileIdByName(workFileName: string): Promise<string | null> {
		const genericCollection = this.dbProvider.getDatabase().workspaces;
		const workFile: RxDocument<WorkspaceModel> = await genericCollection
			.findOne()
			.where(CentralDatabaseKeys.workspaceName)
			.eq(workFileName)
			.exec();

		if (!workFile) {
			return null;
		}

		return workFile.toJSON().workFileId;
	}

	public getEntitiesById$(workfileId: string): Observable<FusionEntity[]> {
		return this.workspacesCollection
			.findOne()
			.where(CentralDatabaseKeys.workFileId)
			.eq(workfileId)
			.$.pipe(map((workspace: WorkspaceModel) => workspace.fusionEntities));
	}

	public any$(): Observable<boolean> {
		// todo: handle this bu checking
		return of(false);
	}

	public getEntityTitle$(workfileId: string, entityName: string): Observable<string> {
		return this.workspacesCollection
			.findOne()
			.where(CentralDatabaseKeys.workFileId)
			.eq(workfileId)
			.$.pipe(
				switchMap(
					(workfile: RxDocument<WorkspaceModel>) => workfile.toJSON()[CentralDatabaseKeys.fusionEntities]
				),
				filter((item: FusionEntity) => item.entity_name === entityName),
				map((item: FusionEntity) => item.entity_title)
			);
	}

	public getEntityPrimaryKey$(workfileId: string, entityName: string): Observable<string[]> {
		return this.workspacesCollection
			.findOne()
			.where(CentralDatabaseKeys.workFileId)
			.eq(workfileId)
			.$.pipe(
				switchMap(
					(workfile: RxDocument<WorkspaceModel>) => workfile.toJSON()[CentralDatabaseKeys.fusionEntities]
				),
				filter((item: FusionEntity) => item.entity_name === entityName),
				map((item: FusionEntity) => item.primary_key),
				first()
			);
	}

	public getEntity$(workfileId: string, entityName: string): Observable<WorkspaceModel | null> {
		return this.workspacesCollection
			.findOne({
				selector: {
					$and: [
						{ [CentralDatabaseKeys.workFileId]: { $eq: workfileId } },
						{
							[CentralDatabaseKeys.fusionEntities]: {
								$elemMatch: { [CentralDatabaseKeys.entityName]: entityName }
							}
						}
					]
				}
			})
			.$.pipe(map((doc: RxDocument<any>) => (doc?.toJSON() ? doc?.toJSON() : null)));
	}

	public isParentEntity$(workfileId: string, entityName: string): Observable<boolean> {
		return this.getEntity$(workfileId, entityName).pipe(
			map((workfile: WorkspaceModel | null) => workfile !== null),
			first()
		);
	}

	public async archiveWorkfile(workFileId: string): Promise<void> {
		const workfile = await this.workspacesCollection
			.findOne()
			.where(CentralDatabaseKeys.workFileId)
			.eq(workFileId)
			.exec();

		try {
			await workfile.patch({ [CentralDatabaseKeys.archived]: true });
		} catch (error) {
			this.loggingService.error(ErrorCodes.ArchiveWorkfileOnExport, error);
			throw error;
		}
	}

	public isReadOnlyMode$(workFileId: string): Observable<boolean> {
		return this.workspacesCollection
			.findOne()
			.where(CentralDatabaseKeys.workFileId)
			.eq(workFileId)
			.$.pipe(map((workfile: RxDocument<WorkspaceModel>) => (workfile ? workfile.toJSON().archived : true)));
	}

	public isWorkfileImportedFromFile$(workFileId: string): Observable<boolean> {
		return this.workspacesCollection
			.findOne()
			.where(CentralDatabaseKeys.workFileId)
			.eq(workFileId)
			.$.pipe(
				map((workfile: RxDocument<WorkspaceModel>) => (workfile ? workfile.toJSON().importedFromFile : false)),
				first()
			);
	}

	public async setWorfileDeleteStatus(workFileId: string): Promise<void> {
		const workfile = await this.workspacesCollection
			.findOne()
			.where(CentralDatabaseKeys.workFileId)
			.eq(workFileId)
			.exec();

		try {
			await workfile.patch({ [CentralDatabaseKeys.pendingDelete]: true });
		} catch (error) {
			this.loggingService.error(ErrorCodes.PendingDeleteWorkfileError, error);
			throw error;
		}
	}

	public async removeConnectionId(connectionId: string): Promise<void> {
		try {
			await this.workspacesCollection
				.find()
				.where(CentralDatabaseKeys.apiConnectionId)
				.eq(connectionId)
				.update({
					$set: {
						[CentralDatabaseKeys.apiConnectionId]: null
					}
				});
		} catch (error) {
			this.loggingService.error(ErrorCodes.ApiConnectionIdRemove, error);
			throw new Error('Delete api connection failed');
		}
	}

	public async setWorkfileConnectionId(workFileId: string, connectionId: string): Promise<void> {
		try {
			await this.workspacesCollection
				.find()
				.where(CentralDatabaseKeys.workFileId)
				.eq(workFileId)
				.update({
					$set: {
						[CentralDatabaseKeys.apiConnectionId]: connectionId
					}
				});
		} catch (error) {
			this.loggingService.error(ErrorCodes.ApiConnectionUpdate, error);
			throw new Error('Update api connection failed');
		}
	}

	public async appendDataToWorkfile(workfileId: string, fileModel: FileModel): Promise<void> {
		try {
		  const rxdbId = Math.floor(Date.now() / 1000)

		//   console.log("APPEND appendDataToWorkfile", newData);
		  await this.workspaceDataImporterService.importData(fileModel.TABLE_DATA, workfileId, rxdbId, true, fileModel);
	
		} catch (error) {
		//   this.logger.error('Append Data to Workfile', error);
			// await this.toastService.presentError(this.translateService.instant('importPage.message.appendDifferentWorkfile'), 3000)
			await this.toastService.presentError(this.translateService.instant(error.message), 3000)
			throw new Error('Failed to append data to the workfile.');
		}
	}

	public async updateWorkfileWithRefData(workfileId: string, newData: any): Promise<void> {
		try {
		  const referenceData = await this.tableRepository.getReferenceData$(workfileId);
	  
		  const mergedData = this.mergeData(referenceData, newData);
	  
		  await this.workspaceDataImporterService.importData(mergedData, workfileId);
		  
		} catch (error) {
		  this.loggingService.error('Failed to update workfile with reference data', error);
		  throw new Error('Failed to update the workfile with reference data.');
		}
	}
	  
	private mergeData(referenceData: any, newData: DataModel[]): DataModel[] {
		return [...referenceData, ...newData];
	}

	
}
